import b4a from 'b4a'
import { domainHash, sign } from '@pine/crypto'

/**
 * Reporting something to whoever runs the server.
 *
 * ── the honest part ────────────────────────────────────────────────────────
 *
 * Everything else in Pine is arranged so that the server cannot read a
 * conversation: rooms are encrypted with keys generated on the devices, and
 * the relay only carries ciphertext. A report is the one deliberate exception
 * — and it only exists because somebody being harassed has to be able to show
 * an operator what was said, and cannot do that with a key the operator does
 * not have.
 *
 * So a report is an *upload*, not a subpoena. The reporter's own device
 * decides what to send, the screen says plainly what is about to be sent, and
 * nothing leaves without somebody pressing the button. Reporting a message in
 * a channel sends that message. Reporting a person sends the conversation with
 * them, because a single line out of context is exactly how a report gets
 * dismissed.
 *
 * The other side of that: this is plain text on a server, readable by whoever
 * runs it. Nobody should report a chat they would not be willing to show, and
 * the UI must never soften that.
 *
 * Signed with the reporter's account key, transcript included, so a report
 * cannot be forged in somebody's name and its contents cannot be edited in
 * flight.
 */

export const REPORT_KINDS = ['message', 'user']
export const REPORT_REASONS = ['spam', 'abuse', 'fraud', 'illegal', 'other']

/** How much of a conversation travels. Also enforced by the server. */
export const TRANSCRIPT_LINES = 100
export const LINE_CHARS = 700

/**
 * A fingerprint of the lines being sent, folded into the signature.
 *
 * Must stay byte-identical to digestTranscript() in
 * server/relay/lib/reports.js — pinned by a parity test, like every other
 * hash the two sides share.
 */
export function digestTranscript (lines) {
  const parts = []
  for (const line of lines || []) {
    parts.push(String(line.at || 0))
    parts.push(String(line.from || ''))
    parts.push(String(line.name || ''))
    parts.push(String(line.text || ''))
  }
  return domainHash('pine/report/lines/v1', ...parts)
}

/** Must stay byte-identical to reportSignable() in the relay. */
export function reportSignable ({ account, at, kind, target, room, reason, note, transcript }) {
  return domainHash(
    'pine/report/v1',
    account,
    String(at),
    String(kind || ''),
    String(target || ''),
    String(room || ''),
    String(reason || ''),
    String(note || ''),
    digestTranscript(transcript)
  )
}

/** Trim a room's messages into what a report is allowed to carry. */
export function transcriptFrom (messages, names = {}) {
  return (messages || [])
    .slice(-TRANSCRIPT_LINES)
    .map((message) => ({
      at: message.timestamp || 0,
      from: message.author || '',
      name: names[message.author] || '',
      // Attachments are named, not sent: a report is about what was said, and
      // uploading somebody's photos to a server to complain about them would
      // be its own kind of wrong.
      text: String(
        message.deleted
          ? ''
          : (message.text || (message.attachment ? '[' + (message.attachment.name || 'file') + ']' : ''))
      ).slice(0, LINE_CHARS)
    }))
}

export class ReportClient {
  constructor ({ url, identity }) {
    this.url = (url || '').replace(/\/+$/, '')
    this.identity = identity
  }

  get enabled () {
    return !!this.url
  }

  /**
   * @returns true when the server took it. A report that cannot be delivered
   *   says so rather than pretending: somebody who thinks they have reported
   *   harassment and has not is worse off than somebody who knows they need
   *   to try again.
   */
  async send ({ kind, target, room = '', reason = 'other', note = '', transcript = [] }) {
    if (!this.enabled) return false

    const at = Date.now()
    const account = this.identity.id
    const lines = (transcript || []).slice(-TRANSCRIPT_LINES)
    const signature = sign(
      reportSignable({ account, at, kind, target, room, reason, note, transcript: lines }),
      this.identity.secretKey
    )

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 20000)
    try {
      const response = await fetch(this.url + '/api/report', {
        method: 'POST',
        // Safelisted, so the Android build does not depend on a preflight.
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          account,
          at,
          kind,
          target,
          room,
          reason,
          note: String(note || '').slice(0, 1000),
          transcript: lines,
          signature: b4a.toString(signature, 'hex')
        }),
        signal: controller.signal
      })
      return response.status === 200
    } catch {
      return false
    } finally {
      clearTimeout(timer)
    }
  }
}
